import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, QrCode, BarChart2, X, Upload, Phone, User, Search, SortAsc, SortDesc, ChevronDown, Filter } from 'lucide-react';
import QRModal from '../components/QRModal';
import StatsModal from '../components/StatsModal';
import Pagination from '../components/Pagination';
import Loader from '../components/Loader';

const API = process.env.REACT_APP_API_URL || '';

// ─── Doctor Form ──────────────────────────────────────────────────────────────
function DoctorForm({ doctor, onSave, onClose }) {
  const [form, setForm] = useState({ name: doctor?.name || '', phone: doctor?.phone || '', specialty: doctor?.specialty || '' });
  const [picture, setPicture] = useState(null);
  const [preview, setPreview] = useState(doctor?.picture ? `${API}${doctor.picture}` : '');
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setPicture(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (picture) fd.append('picture', picture);
      if (doctor) { await axios.put(`/api/doctors/${doctor._id}`, fd); toast.success('Doctor updated!'); }
      else { await axios.post('/api/doctors', fd); toast.success('Doctor created!'); }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2>{doctor ? 'Edit Doctor' : 'Add New Doctor'}</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Profile Picture</label>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              {preview
                ? <img src={preview} alt="preview" style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--purple-400)' }} />
                : <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--purple-100)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><User size={28} color="var(--purple-600)" /></div>
              }
              <div className="upload-area" onClick={() => fileRef.current.click()} style={{ flex: 1, padding: '12px 16px' }}>
                <Upload size={16} style={{ marginBottom: 4, color: 'var(--purple-600)' }} />
                <div style={{ fontSize: '0.8rem' }}>Click to upload photo</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--gray-400)' }}>JPG, JPEG, PNG up to 10MB</div>
              </div>
              <input ref={fileRef} type="file" accept="image/jpg,image/jpeg,image/png" style={{ display: 'none' }} onChange={handleFile} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Full Name *</label>
            <div style={{ position: 'relative' }}>
              <User size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)' }} />
              <input className="form-input" style={{ paddingLeft: 36 }} value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="John Smith" required />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Phone Number</label>
            <div style={{ position: 'relative' }}>
              <Phone size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)' }} />
              <input className="form-input" style={{ paddingLeft: 36 }} value={form.phone}
                onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+92 300 0000000" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Specialty</label>
            <input className="form-input" value={form.specialty}
              onChange={e => setForm(p => ({ ...p, specialty: e.target.value }))} placeholder="e.g. Neurologist" />
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <><div className="spinner" />&nbsp;Saving...</> : 'Save Doctor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Doctors Page ────────────────────────────────────────────────────────
export default function Doctors() {
  const [doctors, setDoctors] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  // Single ref holds ALL filter state — avoids stale closures entirely
  const f = useRef({ page: 1, limit: 50, search: '', sort: 'createdAt', order: 'desc', hasQr: '' });
  const debounce = useRef(null);

  // UI mirror state (controlled inputs only — NOT used for API calls)
  const [uiSearch, setUiSearch] = useState('');
  const [uiSort, setUiSort] = useState('createdAt');
  const [uiOrder, setUiOrder] = useState('desc');
  const [uiHasQr, setUiHasQr] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [qrDoctor, setQrDoctor] = useState(null);
  const [statsDoctor, setStatsDoctor] = useState(null);

  // ── The ONE fetch function — reads from ref, never from state ────────────────
  const doFetch = async (overrides = {}) => {
    Object.assign(f.current, overrides);
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      Object.entries(f.current).forEach(([k, v]) => {
        if (v !== '' && v != null) qs.set(k, String(v));
      });
      const res = await axios.get(`/api/doctors?${qs}`);
      // Backend always returns { data: [], pagination: {} }
      const resp = res.data || {};
      setDoctors(Array.isArray(resp.data) ? resp.data : []);
      setPagination(
        resp.pagination && typeof resp.pagination === 'object'
          ? resp.pagination
          : { page: 1, limit: 50, total: 0, totalPages: 1 }
      );
    } catch {
      toast.error('Failed to load doctors');
      setDoctors([]);
      setPagination({ page: 1, limit: 50, total: 0, totalPages: 1 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { doFetch(); }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────────
  // Search: update UI immediately, debounce the API call
  const handleSearch = (val) => {
    setUiSearch(val);
    f.current.search = val; // keep ref in sync so debounced call uses latest value
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => doFetch({ page: 1 }), 500);
  };

  const handleSort = (field) => {
    const newOrder = uiSort === field && uiOrder === 'desc' ? 'asc' : 'desc';
    setUiSort(field);
    setUiOrder(newOrder);
    doFetch({ sort: field, order: newOrder, page: 1 });
  };

  const handleHasQr = (val) => {
    setUiHasQr(val);
    doFetch({ hasQr: val, page: 1 });
  };

  const handleClear = () => {
    clearTimeout(debounce.current);
    setUiSearch(''); setUiSort('createdAt'); setUiOrder('desc'); setUiHasQr('');
    doFetch({ search: '', sort: 'createdAt', order: 'desc', hasQr: '', page: 1 });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this doctor?')) return;
    try { await axios.delete(`/api/doctors/${id}`); toast.success('Deleted'); doFetch(); }
    catch { toast.error('Delete failed'); }
  };

  // SortBtn defined inside component so it reads uiSort/uiOrder correctly
  const SortBtn = ({ field, label }) => {
    const active = uiSort === field;
    return (
      <button onClick={() => handleSort(field)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 7,
          border: `1.5px solid ${active ? 'var(--purple-400)' : 'var(--gray-200)'}`,
          background: active ? 'var(--purple-100)' : 'white',
          color: active ? 'var(--purple-800)' : 'var(--gray-600)',
          fontSize: '0.78rem', fontWeight: active ? 600 : 400, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif'
        }}>
        {label}
        {active
          ? (uiOrder === 'asc' ? <SortAsc size={13} /> : <SortDesc size={13} />)
          : <SortAsc size={13} style={{ opacity: 0.3 }} />}
      </button>
    );
  };

  const activeFilters = [uiSearch, uiHasQr].filter(Boolean).length;
  const pg = pagination; // short alias

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Doctors</h1>
          <p>{pg.total} doctor profiles registered</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditing(null); setShowForm(true); }}>
          <Plus size={16} /> Add Doctor
        </button>
      </div>

      {/* ── Toolbar ── */}
      <div className="card" style={{ marginBottom: 20, padding: '14px 18px' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 180 }}>
            <Search size={15} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)' }} />
            <input className="form-input" placeholder="Search name, phone, specialty..."
              value={uiSearch} onChange={e => handleSearch(e.target.value)}
              style={{ paddingLeft: 34, margin: 0 }} />
          </div>

          {/* QR filter */}
          <div style={{ position: 'relative' }}>
            <Filter size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: uiHasQr ? 'var(--purple-800)' : 'var(--gray-400)', pointerEvents: 'none' }} />
            <select value={uiHasQr} onChange={e => handleHasQr(e.target.value)}
              style={{ paddingLeft: 28, paddingRight: 28, height: 38, borderRadius: 10, border: `1.5px solid ${uiHasQr ? 'var(--purple-400)' : 'var(--gray-200)'}`, fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', cursor: 'pointer', outline: 'none', color: uiHasQr ? 'var(--purple-800)' : 'var(--gray-600)', background: uiHasQr ? 'var(--purple-100)' : 'white', appearance: 'none' }}>
              <option value="">All QR Status</option>
              <option value="true">QR Generated</option>
              <option value="false">No QR</option>
            </select>
            <ChevronDown size={13} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--gray-400)' }} />
          </div>

          {/* Sort */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)', whiteSpace: 'nowrap' }}>Sort:</span>
            <SortBtn field="name" label="Name" />
            <SortBtn field="createdAt" label="Date" />
            <SortBtn field="specialty" label="Specialty" />
          </div>

          {/* Clear */}
          {activeFilters > 0 && (
            <button className="btn btn-sm btn-outline" onClick={handleClear}>
              <X size={13} /> Clear ({activeFilters})
            </button>
          )}
        </div>
      </div>

      {/* ── Cards ── */}
      {loading ? (
        <Loader />
      ) : (
        <>
          <Pagination
            page={pg.page}
            limit={pg.limit}
            total={pg.total}
            onPageChange={p => doFetch({ page: p })}
            onLimitChange={l => doFetch({ limit: l, page: 1 })}
          />
          {doctors.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 60 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>👨‍⚕️</div>
              <h3 style={{ marginBottom: 8 }}>No doctors found</h3>
              <p style={{ color: 'var(--gray-400)', marginBottom: 20 }}>
                {uiSearch || uiHasQr ? 'Try adjusting your filters' : 'Create your first doctor profile'}
              </p>
              {!uiSearch && !uiHasQr && (
                <button className="btn btn-primary" onClick={() => { setEditing(null); setShowForm(true); }}>
                  <Plus size={16} /> Add Doctor
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
              {doctors.map(doc => (
                <div className="doctor-card" key={doc._id}>
                  <div style={{ padding: '18px 18px 14px', borderBottom: '1px solid var(--gray-100)' }}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      {doc.picture
                        ? <img src={`${API}${doc.picture}`} alt={doc.name} style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--purple-200)', flexShrink: 0 }} />
                        : <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--purple-100)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><User size={22} color="var(--purple-600)" /></div>
                      }
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.name}</div>
                        {doc.specialty && <div style={{ fontSize: '0.75rem', color: 'var(--purple-700)', fontWeight: 500, marginBottom: 2 }}>{doc.specialty}</div>}
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{doc.phone}</div>
                      </div>
                    </div>
                  </div>
                  <div style={{ padding: '10px 14px', display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'space-between' }}>
                    <span className={`badge ${doc.qrCode ? 'badge-green' : 'badge-purple'}`}>
                      {doc.qrCode ? '✓ QR Ready' : 'No QR'}
                    </span>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-sm" style={{ background: 'var(--purple-100)', color: 'var(--purple-800)', border: 'none' }} onClick={() => setQrDoctor(doc)} title="QR Code"><QrCode size={13} /></button>
                      <button className="btn btn-sm" style={{ background: '#E0F2FE', color: '#0369A1', border: 'none' }} onClick={() => setStatsDoctor(doc)} title="Analytics"><BarChart2 size={13} /></button>
                      <button className="btn btn-sm btn-outline" onClick={() => { setEditing(doc); setShowForm(true); }} title="Edit"><Edit2 size={13} /></button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(doc._id)} title="Delete"><Trash2 size={13} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showForm && <DoctorForm doctor={editing} onSave={() => { setShowForm(false); doFetch(); }} onClose={() => setShowForm(false)} />}
      {qrDoctor && <QRModal doctor={qrDoctor} onClose={() => setQrDoctor(null)} onUpdate={updated => setDoctors(prev => prev.map(d => d._id === updated._id ? updated : d))} />}
      {statsDoctor && <StatsModal doctor={statsDoctor} onClose={() => setStatsDoctor(null)} />}
    </div>
  );
}

